import { TCustomAxiosPaginationResponse, TPagination } from '@/services/api';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface IUsePaginationParams<TData, TSearchFilterType, TSearchFilterText> {
  query: (
    page: number,
    searchFilterType: TSearchFilterType,
    searchFilterText: TSearchFilterText,
    sortingColumn?: string,
    sortingDirection?: string,
    pageSize?: number,
  ) => Promise<TCustomAxiosPaginationResponse<TData>>;
  queryKey: string | string[];
  searchFilterType: TSearchFilterType;
  searchFilterText: TSearchFilterText;
  sortingColumn?: string;
  sortingDirection?: string;
  pageSize?: number;
}

/**
 *
 * @param query - the query to be executed. Must receive `page: number` param and return ApiResponseType<T>
 * @returns isLoading: boolean; isSuccess: boolean; data: TData | undefined; isError: boolean; error: Error | null; pagination: TPagination
 */
export const usePaginatedSearch = <TData, TSearchFilterType, TSearchFilterText>({
  query,
  queryKey,
  searchFilterType,
  searchFilterText,
  sortingColumn,
  sortingDirection,
  pageSize,
}: IUsePaginationParams<TData, TSearchFilterType, TSearchFilterText>) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [searchFilterType, searchFilterText, sortingColumn, sortingDirection, pageSize]);

  const { isLoading, isSuccess, data, isError, error, isRefetching, refetch } = useQuery({
    queryKey: [queryKey, page, searchFilterType, searchFilterText, sortingColumn, sortingDirection, pageSize],
    queryFn: async () => {
      const queryToCall = query(
        page,
        searchFilterType,
        searchFilterText,
        sortingColumn,
        sortingDirection,
        pageSize,
      );
      const response = await queryToCall;
      return response;
    },
    placeholderData: keepPreviousData,
  });

  const lastPage = data?.pagination.totalPages ?? 1;

  const getFirstPage = () => setPage(1);
  const getLastPage = () => setPage(lastPage);
  const getNextPage = () => setPage((prev) => (prev < lastPage ? prev + 1 : lastPage));
  const getPreviousPage = () => setPage((prev) => (prev > 1 ? prev - 1 : 1));

  return {
    isLoading,
    isSuccess,
    data: data?.data,
    isError,
    error,
    refetch,
    isRefetching,
    pagination: {
      getFirstPage,
      getNextPage,
      getPreviousPage,
      getLastPage,
      totalPages: data?.pagination.totalPages ?? 0,
      currentPage: page,
      totalItems: data?.pagination.totalItems,
    } as TPagination,
  };
};
